import { createAsyncThunk, createSlice, Dispatch, PayloadAction } from '@reduxjs/toolkit'
import { isEmpty } from 'lodash'

import { landBoardAxios } from '@Root/utils/landAxios'
import MyhouseBoardParam from '@Types/entities/MyhouseBoardParam'
import MyhouseArticleImageResponse from '@Types/entities/MyhouseArticleImageResponse'
import ClovaOcrResponse from '@Types/entities/ClovaOcrResponse'
import FetchResponse from '@Types/inner/FetchResponse'
import { FETCH_STATUS } from '@Root/contants'
import PenaltyResponse from '@Types/entities/PenaltyResponse'

export const fetchBoardRegister = createAsyncThunk('/board/register', async ({ param, files, dispatch }: { param: MyhouseBoardParam; files: File[]; dispatch: Dispatch }) => {
  const formData = new FormData()
  const representImageYnList: string[] = []
  const imageOrderList: string[] = []

  files.forEach((file, i) => {
    const num = i
    formData.append('files', file)
    imageOrderList.push(String(num + 1))
    if (i === 0) representImageYnList.push('Y')
    else representImageYnList.push('N')
  })

  formData.append('complexNo', JSON.stringify(param.complexNo))
  formData.append('bbsTypeCode', param.bbsTypeCode)
  formData.append('contents', param.contents ? param.contents : '')
  formData.append('copyYn', JSON.stringify(param.copyYn))

  if (param.bbsTypeCode === 'NOTICE') {
    formData.append('title', param.title)
    formData.append('openingYn', JSON.stringify(param.openingYn))
    formData.append('noticeYn', JSON.stringify(param.noticeYn))
    formData.append('category', param.category)
  }

  representImageYnList.forEach((item, i) => {
    formData.append('representImageYnList[' + i + ']', representImageYnList[i])
    formData.append('imageOrderList[' + i + ']', imageOrderList[i])
  })

  return await (
    await landBoardAxios.post(`/myhouse/board/articles`, formData, {
      onUploadProgress: (progressEvent) => {
        const percentage = (progressEvent.loaded * 100) / progressEvent.total
        dispatch(updatePercentage(percentage))
      },
    })
  ).data
})

export const fetchBoardModify = createAsyncThunk('/board/modify', async ({ param, files, dispatch }: { param: MyhouseBoardModifyParam; files: File[]; dispatch: Dispatch }) => {
  const formData = new FormData()
  const representImageYnList: string[] = []
  const imageOrderList: string[] = []

  param.imageList.forEach((element, index) => {
    imageOrderList.push(String(index))
    if (index === 0) {
      representImageYnList.push('Y')
    } else {
      representImageYnList.push('N')
    }
  })

  files.forEach((file, i) => {
    formData.append('files', file)
    imageOrderList.push(String(param.imageList.length + i))
    if (i === 0 && param.imageList.length === 0) {
      representImageYnList.push('Y')
    } else {
      representImageYnList.push('N')
    }
  })

  formData.append('complexNo', JSON.stringify(param.complexNo))
  formData.append('bbsTypeCode', param.bbsTypeCode)
  formData.append('contents', param.contents ? param.contents : '')
  formData.append('copyYn', JSON.stringify(param.copyYn))
  formData.append('bbsSeq', param.bbsSeq)
  formData.append('imageListString', JSON.stringify(param.imageList))

  if (param.bbsTypeCode === 'NOTICE') {
    formData.append('title', param.title ? param.title : '')
    formData.append('openingYn', JSON.stringify(param.openingYn))
    formData.append('noticeYn', JSON.stringify(param.noticeYn))
    formData.append('category', param.category)
  }

  representImageYnList.forEach((item, i) => {
    formData.append('representImageYnList[' + i + ']', representImageYnList[i])
    formData.append('imageOrderList[' + i + ']', imageOrderList[i])
  })

  return await (
    await landBoardAxios.post(`/myhouse/board/articles/${param.bbsSeq}`, formData, {
      onUploadProgress: (progressEvent) => {
        const percentage = (progressEvent.loaded * 100) / progressEvent.total
        dispatch(updatePercentage(percentage))
      },
    })
  ).data
})

export const fetchImageRecognition = createAsyncThunk('/board/imageRecognition', async (file: File) => {
  const formData = new FormData()
  formData.append('file', file)

  return await (await landBoardAxios.post(`/myhouse/board/image-detection`, formData, {})).data
})

export const fetchBoardDelete = createAsyncThunk(
  '/board/delete',
  async (params: MyhouseBoardDeleteParam) => await (await landBoardAxios.delete(`/myhouse/board/articles/${params.sequence}`)).data,
)

export const fetchPenalty = createAsyncThunk('/board/fetchPenalty', async () => await (await landBoardAxios.get(`/myhouse/board/penalty`)).data)

interface RegisterInterface {
  bbsTypeCode: string
  noticeYn: boolean
  openingYn: boolean
  copyYn: boolean
  title: string
  contents: string
  category: string
  percentage: number
  fileSize: number
  penalty: FetchResponse<{
    code?: string
    fromDate?: string
    toDate?: string
  }>
}

export type MyhouseBoardState = FetchResponse<RegisterInterface>
export type MyhouseBoardModifyParam = {
  imageList: MyhouseArticleImageResponse[]
  sequence: number
} & MyhouseBoardParam

export type MyhouseBoardDeleteParam = {
  sequence: number
}

const initialState: MyhouseBoardState = {
  bbsTypeCode: '',
  noticeYn: false,
  openingYn: false,
  copyYn: true,
  title: '',
  contents: '',
  category: '',
  percentage: 0,
  fileSize: 0,
  fetchStatus: FETCH_STATUS.INITIAL,
  penalty: {
    fetchStatus: FETCH_STATUS.INITIAL,
  },
}

export const boardRegisterSlice = createSlice({
  name: 'board',
  initialState,
  reducers: {
    initializeState: (state: MyhouseBoardState) => {
      Object.assign(state, initialState)
    },
    updateSelectCategory: (state: MyhouseBoardState, action: PayloadAction<string>) => {
      state.category = action.payload
    },
    updateTitle: (state: MyhouseBoardState, action: PayloadAction<string>) => {
      state.title = action.payload
    },
    updateContents: (state: MyhouseBoardState, action: PayloadAction<string>) => {
      state.contents = action.payload
    },
    updateNoticeYn: (state: MyhouseBoardState, action: PayloadAction<boolean>) => {
      state.noticeYn = action.payload
    },
    updateOpeningYn: (state: MyhouseBoardState, action: PayloadAction<boolean>) => {
      state.openingYn = action.payload
    },
    updateCopyYn: (state: MyhouseBoardState, action: PayloadAction<boolean>) => {
      state.copyYn = action.payload
    },
    updateBbsTypeCode: (state: MyhouseBoardState, action: PayloadAction<string>) => {
      state.bbsTypeCode = action.payload
    },
    updateFetchStatus: (state: MyhouseBoardState, action: PayloadAction<FETCH_STATUS>) => {
      state.fetchStatus = action.payload
    },
    updatePercentage: (state: MyhouseBoardState, action: PayloadAction<number>) => {
      state.percentage = action.payload
    },
    updateFileSize: (state: MyhouseBoardState, action: PayloadAction<number>) => {
      state.fileSize = action.payload
    },
  },
  extraReducers: {
    [fetchBoardRegister.fulfilled.type]: (state: MyhouseBoardState, action: PayloadAction<MyhouseBoardParam>) => {
      // Object.assign(state, initialState)
      state.fetchStatus = FETCH_STATUS.SUCCESS
    },
    [fetchBoardModify.fulfilled.type]: (state: MyhouseBoardState, action: PayloadAction<MyhouseBoardModifyParam>) => {
      // Object.assign(state, initialState)
      state.fetchStatus = FETCH_STATUS.SUCCESS
    },
    [fetchBoardDelete.fulfilled.type]: (state: MyhouseBoardState, action: PayloadAction<MyhouseBoardModifyParam>) => {
      // Object.assign(state, initialState)
      state.fetchStatus = FETCH_STATUS.SUCCESS
    },
    [fetchImageRecognition.fulfilled.type]: (state: MyhouseBoardState, action: PayloadAction<ClovaOcrResponse>) => {
      const ocrTitle = action.payload.title
      if (ocrTitle && isEmpty(state.title)) {
        state.title = ocrTitle
      }
    },
    [fetchBoardRegister.pending.type]: (state: MyhouseBoardState) => {
      state.fetchStatus = FETCH_STATUS.PENDING
    },
    [fetchBoardRegister.rejected.type]: (state: MyhouseBoardState) => {
      state.fetchStatus = FETCH_STATUS.FAILURE
    },

    [fetchBoardModify.pending.type]: (state: MyhouseBoardState) => {
      state.fetchStatus = FETCH_STATUS.PENDING
    },
    [fetchBoardModify.rejected.type]: (state: MyhouseBoardState) => {
      state.fetchStatus = FETCH_STATUS.FAILURE
    },

    [fetchBoardDelete.pending.type]: (state: MyhouseBoardState) => {
      state.fetchStatus = FETCH_STATUS.PENDING
    },
    [fetchBoardDelete.rejected.type]: (state: MyhouseBoardState) => {
      state.fetchStatus = FETCH_STATUS.FAILURE
    },

    [fetchImageRecognition.fulfilled.type]: (state: MyhouseBoardState, action: PayloadAction<ClovaOcrResponse>) => {
      const ocrTitle = action.payload.title
      if (ocrTitle && isEmpty(state.title)) {
        state.title = ocrTitle
      }
    },
    [fetchPenalty.fulfilled.type]: (state: MyhouseBoardState, action: PayloadAction<PenaltyResponse>) => {
      // PenaltyResponse
      Object.assign(state.penalty, action.payload)
      state.penalty.fetchStatus = FETCH_STATUS.SUCCESS
    },
    [fetchPenalty.pending.type]: (state: MyhouseBoardState) => {
      state.penalty.fetchStatus = FETCH_STATUS.PENDING
    },
    [fetchPenalty.rejected.type]: (state: MyhouseBoardState) => {
      state.penalty.fetchStatus = FETCH_STATUS.FAILURE
    },
  },
})

export const {
  updateSelectCategory,
  updateTitle,
  updateContents,
  updateNoticeYn,
  updateOpeningYn,
  updateCopyYn,
  updateBbsTypeCode,
  updateFetchStatus,
  initializeState,
  updatePercentage,
  updateFileSize,
} = boardRegisterSlice.actions
export default boardRegisterSlice.reducer
